import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, Typography } from '@mui/material';
import { DataGrid, GridActionsColDef, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { Organisatie } from '../../../models/organisatie/organisatie';
import { ListParams } from '../../../models/listParams';

import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';

import SharedOrganisatiesActionMenu from './SharedOrganisatiesActionMenu';

import { planManagementService } from '../../../services/plan-management.service';
import { authService } from '../../../services/auth.service';
import { pageSizeStorageKey } from '../../../services/utils/localStorageKeys';
import { usePersistedPageSize } from '../../../hooks/usePersistedPageSize';

const CustomNoRowsOverlay = () => {
  return (
    <Paper
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        width: "100%",
        padding: 2,
        backgroundColor: "transparent",
      }}
      elevation={0}
    >
      <Typography variant="body1">Niets gevonden</Typography>
    </Paper>
  );
};

interface SharedOrganisatiesByPlanProps {
  planId: string;
}

const SharedOrganisatiesByPlan: React.FC<SharedOrganisatiesByPlanProps> = ({ 
  planId
}) => {
  const navigate = useNavigate();

  const [organisaties, setOrganisaties] = useState<Organisatie[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);
 
  const [pageIndex, setPageIndex] = useState(0);
  const { pageSize, setPageSize } = usePersistedPageSize(pageSizeStorageKey('shared.organisaties.byplan'));
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize,
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const getUserRole = async () => {
    const role = await authService.getRole();
    setUserRole(role);
  };

  const fetchOrganisaties = useCallback(async () => {
    setLoading(true);
    try {
      const params: ListParams = {
        offset: pageIndex * pageSize, 
        limit: pageSize, 
      };

      let organisaties: any[] = [];
      if (planId) {
        const data = await planManagementService.getOrganisationsSharedWithPlan(planId, params);

        organisaties = data.items;

        setOrganisaties(organisaties);
        setTotalCount(data.count);
      }
      else {
        setOrganisaties([]);
      }
      
      setLoading(false);
    }
    catch (error: any) {
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError(error.message || 'Er is een fout opgetreden');
      setLoading(false);
    }
  }, [pageIndex, pageSize, planId, navigate]);

  const gridColumns = useMemo<(GridColDef<Organisatie> | GridActionsColDef<Organisatie>)[]>(() => {
    const baseColumns: (GridColDef<Organisatie> | GridActionsColDef<Organisatie>)[] = [
      { field: 'id', headerName: 'Id', flex: 2 },
      { field: 'displayName', headerName: 'Naam', flex: 3 },
    ];
    
    baseColumns.push({ field: 'organizationType', headerName: 'Type', flex: 2 });
    // { field: 'name', headerName: 'Organisatie', flex: 2 },
    baseColumns.push({
      field: 'actions',
      headerName: 'Acties',
      renderHeader: () => <></>,
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<Organisatie>) => (
        <SharedOrganisatiesActionMenu params={params} planId={planId} isZonderGebied={false} onOrganisatiesUpdate={fetchOrganisaties} />
      ),
    });
  
    return baseColumns;
  }, [fetchOrganisaties, planId]);

  useEffect(() => { 
    getUserRole(); 
    setReady(true); 
  }, []);

  useEffect(() => {
    if (!ready) return;
    fetchOrganisaties();
  }, [ready, fetchOrganisaties]);

  const onChangePagination = async ({ page, pageSize }: { page: number, pageSize: number }) => {
    setPaginationModel({ page, pageSize });
    setPageIndex(page);
    setPageSize(pageSize);
  };

  return (
    <>
      {organisaties.length > 0 &&  (
        <div className="grid-responsive">
          <Paper sx={{ width: '100%' }}>
            <DataGrid
              rows={organisaties}
              columns={gridColumns}
              disableColumnFilter
              disableColumnSorting 
              disableRowSelectionOnClick
              loading={false}
              rowCount={totalCount}
              paginationMode="server"
              pageSizeOptions={[5, 10, 25, 50, 100]}
              initialState={{
                pagination: {
                  paginationModel: {
                    page: pageIndex,
                    pageSize: pageSize
                  }
                }
              }}
              onPaginationModelChange={onChangePagination}
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
                loadingOverlay: () => null // or your own minimal custom component
              }}
              // localeText={{
              //   noRowsLabel: "Niets gevonden",
              // slots={{
              //   noRowsOverlay: CustomNoRowsOverlay,
              // }}
              sx={{ border: 0 }}
            />
          </Paper>
        </div> 
      )}

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}

    </>
  );
};

export default SharedOrganisatiesByPlan;
